import type { AirQualityData, CityAirQuality } from "@/lib/conditions/air-quality";

function aqiBadgeColor(aqi: number): string {
  if (aqi <= 20) return "bg-emerald-100 text-emerald-800";
  if (aqi <= 40) return "bg-lime-100 text-lime-800";
  if (aqi <= 60) return "bg-amber-100 text-amber-800";
  if (aqi <= 80) return "bg-orange-100 text-orange-800";
  return "bg-red-100 text-red-800";
}

type PollutantKey = "pm25" | "pm10" | "no2" | "o3" | "so2";

const CITY_COLORS: string[] = [
  "bg-rose-400",
  "bg-amber-400",
  "bg-violet-400",
  "bg-emerald-400",
  "bg-sky-400",
  "bg-orange-400",
];

const CITY_TEXT_COLORS: string[] = [
  "text-rose-500",
  "text-amber-500",
  "text-violet-500",
  "text-emerald-500",
  "text-sky-500",
  "text-orange-500",
];

const POLLUTANT_CONFIG: {
  key: PollutantKey;
  label: string;
  whoLimit: number;
  description: string;
}[] = [
  { key: "pm25", label: "PM2.5", whoLimit: 15, description: "Fine particles that enter your lungs" },
  { key: "pm10", label: "PM10", whoLimit: 45, description: "Coarse dust and pollen particles" },
  { key: "no2", label: "NO₂", whoLimit: 25, description: "Vehicle exhaust and combustion" },
  { key: "so2", label: "SO₂", whoLimit: 40, description: "Industrial and volcanic emissions" },
];

const POLLUTION_KEYS: PollutantKey[] = ["pm25", "pm10", "no2", "so2"];

function qualityTag(value: number, whoLimit: number): { label: string; color: string } {
  const ratio = value / whoLimit;
  if (ratio <= 0.33) return { label: "Excellent", color: "text-emerald-600" };
  if (ratio <= 0.66) return { label: "Good", color: "text-lime-600" };
  if (ratio <= 1.0) return { label: "Moderate", color: "text-amber-600" };
  if (ratio <= 1.5) return { label: "Poor", color: "text-orange-600" };
  return { label: "Unhealthy", color: "text-red-600" };
}

function PollutantRow({
  config,
  resortValue,
  cities,
}: {
  config: (typeof POLLUTANT_CONFIG)[number];
  resortValue: number;
  cities: CityAirQuality[];
}) {
  const cityValues = cities.map((c, i) => ({
    name: c.name,
    value: c[config.key],
    colorClass: CITY_COLORS[i] ?? "bg-slate-400",
    textClass: CITY_TEXT_COLORS[i] ?? "text-slate-500",
  }));
  const allValues = [resortValue, ...cityValues.map((c) => c.value), config.whoLimit];
  const maxVal = Math.max(...allValues, 1);

  const resortPct = (resortValue / maxVal) * 100;
  const whoPct = (config.whoLimit / maxVal) * 100;
  const tag = qualityTag(resortValue, config.whoLimit);

  const resortBarColor =
    resortValue <= config.whoLimit * 0.5
      ? "bg-emerald-400"
      : resortValue <= config.whoLimit
        ? "bg-lime-400"
        : resortValue <= config.whoLimit * 1.5
          ? "bg-amber-400"
          : "bg-red-400";

  return (
    <div className="py-4 border-b border-border-light last:border-b-0">
      <div className="flex items-start justify-between mb-1">
        <div>
          <span className="text-[13px] font-semibold text-navy">{config.label}</span>
          <span className="text-[11px] text-text-muted ml-2">{config.description}</span>
        </div>
        <div className="text-right shrink-0 ml-3">
          <span className="text-[13px] font-bold text-navy tabular-nums">{resortValue.toFixed(1)}</span>
          <span className="text-[10px] text-text-muted ml-0.5">µg/m³</span>
        </div>
      </div>

      <div className="text-[11px] font-semibold mb-2">
        <span className={tag.color}>{tag.label}</span>
        <span className="text-text-muted font-normal"> · lower is better · WHO limit {config.whoLimit}</span>
      </div>

      <div className="relative h-3 rounded-full bg-slate-100 overflow-visible mb-2.5">
        <div
          className={`h-full rounded-full ${resortBarColor} transition-all relative z-10`}
          style={{ width: `${Math.min(resortPct, 100)}%` }}
        />
        <div
          className="absolute top-[-3px] bottom-[-3px] w-[2px] bg-red-300 z-20 rounded-full"
          style={{ left: `${Math.min(whoPct, 100)}%` }}
        />
        {cityValues.map((c) => {
          const pct = (c.value / maxVal) * 100;
          return (
            <div
              key={c.name}
              className="absolute top-[-6px] z-30 flex flex-col items-center"
              style={{ left: `${Math.min(pct, 100)}%`, transform: "translateX(-50%)" }}
            >
              <span className={`text-[9px] font-semibold ${c.textClass} whitespace-nowrap leading-none`}>
                {c.name.charAt(0)}
              </span>
              <div className={`w-[6px] h-[6px] rounded-full ${c.colorClass} border border-white mt-[1px]`} />
            </div>
          );
        })}
      </div>

    </div>
  );
}

function healthSummary(pctCleaner: number): string {
  if (pctCleaner >= 70) return "Significantly cleaner air than a typical city. Ideal for outdoor exercise with minimal respiratory strain.";
  if (pctCleaner >= 40) return "Noticeably cleaner air than a typical city. Good conditions for prolonged outdoor activity.";
  if (pctCleaner >= 15) return "Somewhat cleaner than a typical city. Air quality is unlikely to affect most people.";
  if (pctCleaner >= 0) return "Air quality is similar to the comparison cities.";
  return "Air quality is currently worse than a typical city. Consider shorter outdoor sessions.";
}

export function AirQualityPanel({
  data,
}: {
  data: AirQualityData;
}) {
  const pctCleaner = (() => {
    if (data.cities.length === 0) return null;
    const resortAvg = POLLUTION_KEYS.reduce((s, k) => s + data[k], 0) / POLLUTION_KEYS.length;
    const cityAvg =
      data.cities.reduce(
        (s, c) => s + POLLUTION_KEYS.reduce((s2, k) => s2 + c[k], 0) / POLLUTION_KEYS.length,
        0
      ) / data.cities.length;
    if (cityAvg <= 0) return null;
    return Math.round(((cityAvg - resortAvg) / cityAvg) * 100);
  })();

  const resortPollutionAvg = POLLUTION_KEYS.reduce((s, k) => s + data[k], 0) / POLLUTION_KEYS.length;
  const cityPollutionAvgs = data.cities.map((c, i) => ({
    name: c.name,
    avg: POLLUTION_KEYS.reduce((s, k) => s + c[k], 0) / POLLUTION_KEYS.length,
    colorClass: CITY_COLORS[i] ?? "bg-slate-400",
    textClass: CITY_TEXT_COLORS[i] ?? "text-slate-500",
  }));
  const maxPollutionAvg = Math.max(resortPollutionAvg, ...cityPollutionAvgs.map((c) => c.avg), 1);

  return (
    <div className="bg-white rounded-[16px] border border-border-light p-5 md:p-7">
      <h3 className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted mb-4">
        Air quality
      </h3>

      {/* Summary */}
      <div className="flex flex-wrap items-start gap-4 md:gap-6 mb-6">
        <div className="text-center shrink-0">
          <div
            className={`inline-block rounded-[10px] px-4 py-2 ${aqiBadgeColor(data.aqi)}`}
          >
            <div className="font-serif text-[28px] font-bold leading-none tabular-nums">
              {data.aqi}
            </div>
            <div className="text-[10px] font-semibold uppercase tracking-[1px] mt-0.5">
              AQI
            </div>
          </div>
          <div className="text-[12px] font-semibold text-navy mt-1.5">
            {data.aqiLabel}
          </div>
        </div>

        {pctCleaner !== null && (
          <div className="flex-1 min-w-0">
            <p className="text-[15px] md:text-[16px] font-serif text-navy leading-[1.5]">
              {pctCleaner > 0 ? (
                <>
                  The air here is <span className="font-bold text-emerald-600">{pctCleaner}% cleaner</span> than
                  the average comparison city right now.
                </>
              ) : pctCleaner === 0 ? (
                <>Air quality is on par with the average comparison city right now.</>
              ) : (
                <>
                  Air quality is currently <span className="font-bold text-amber-600">{Math.abs(pctCleaner)}% worse</span> than
                  the average comparison city.
                </>
              )}
            </p>
            <p className="text-[12.5px] text-text-muted mt-1 leading-[1.5]">
              {healthSummary(pctCleaner)}
            </p>
          </div>
        )}
      </div>

      {/* Pollution comparison */}
      {data.cities.length > 0 && (
        <div className="bg-slate-50 rounded-[12px] p-4 mb-5">
          <div className="text-[11px] font-semibold uppercase tracking-[1px] text-text-muted mb-1">
            Pollution level (lower is better)
          </div>
          <div className="text-[10px] text-text-muted mb-2.5">
            Average of PM2.5, PM10, NO₂ and SO₂
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <span className="text-[12px] font-semibold text-navy w-16 shrink-0">Here</span>
              <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
                <div
                  className={`h-full rounded-full ${pctCleaner !== null && pctCleaner > 0 ? "bg-emerald-400" : pctCleaner !== null && pctCleaner >= -15 ? "bg-amber-400" : "bg-orange-400"}`}
                  style={{ width: `${Math.min((resortPollutionAvg / maxPollutionAvg) * 100, 100)}%` }}
                />
              </div>
              <span className="text-[12px] font-bold text-navy tabular-nums w-7 text-right">{resortPollutionAvg.toFixed(0)}</span>
            </div>
            {cityPollutionAvgs.map((city) => (
              <div key={city.name} className="flex items-center gap-3">
                <span className={`text-[12px] font-medium w-16 shrink-0 ${city.textClass}`}>
                  {city.name}
                </span>
                <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${city.colorClass}`}
                    style={{ width: `${Math.min((city.avg / maxPollutionAvg) * 100, 100)}%` }}
                  />
                </div>
                <span className="text-[12px] font-semibold text-navy tabular-nums w-7 text-right">{city.avg.toFixed(0)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pollutant breakdown */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-[11px] font-semibold uppercase tracking-[1px] text-text-muted">
            Pollutant breakdown
          </h4>
          <span className="text-[10px] text-text-muted">All values: lower is better</span>
        </div>
        {POLLUTANT_CONFIG.map((p) => (
          <PollutantRow
            key={p.key}
            config={p}
            resortValue={data[p.key]}
            cities={data.cities}
          />
        ))}
      </div>

      {/* Ozone */}
      <div className="mt-5 pt-4 border-t border-border-light/60">
        <div className="flex items-start justify-between mb-1.5">
          <span className="text-[12px] font-medium text-text-muted">O₃ (Ozone)</span>
          <span className="text-[12px] font-semibold text-text-muted tabular-nums">
            {data.o3.toFixed(1)} <span className="text-[10px] font-normal">µg/m³</span>
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-2">
          <div
            className="h-full rounded-full bg-slate-300"
            style={{ width: `${Math.min((data.o3 / 150) * 100, 100)}%` }}
          />
        </div>
        <p className="text-[10.5px] text-text-muted/70 leading-[1.5]">
          Not a pollutant. Ozone is naturally higher at altitude and near the coast, and lower in cities
          where traffic exhaust breaks it down. High readings here typically indicate clean air. Can cause
          short-term airway irritation during intense exercise but is not linked to the long-term health
          risks of particulate pollution.
        </p>
      </div>

      <p className="text-[10px] text-text-muted/60 mt-4">
        Additive health score: each pollutant contributes points relative to its WHO 2021 guideline and long-term health
        impact (PM2.5 9, NO₂ 5, O₃ 3, PM10 2, SO₂ 1 at WHO limits). Data via Open-Meteo. City markers show live readings.
        Red line marks the WHO guideline. Updated{" "}
        {new Date(data.fetchedAt).toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        })}
      </p>
    </div>
  );
}
